import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../../utils/axios';
import { ArrowRightIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { OrderTracking } from '../../components/tracking';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        const response = await axios.get(`/api/orders/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setOrder(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load order');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrder();
    }
  }, [orderId]);

  const formatPrice = (price) => {
    return `₪${parseFloat(price || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-gray-200 rounded w-1/4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700 mb-4">{error || 'הזמנה לא נמצאה'}</p>
            <button
              onClick={() => navigate('/orders')}
              className="btn btn-primary"
            >
              חזור להזמנות
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4 space-x-reverse">
            <button
              onClick={() => navigate('/orders')}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowRightIcon className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">פרטי הזמנה</h1>
              <p className="text-gray-600 mt-1">הזמנה #{order.order_number}</p>
            </div>
          </div>
          <button
            onClick={() => window.print()}
            className="btn btn-outline flex items-center"
          >
            <PrinterIcon className="w-5 h-5 ml-2" />
            הדפס
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Items */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">פריטי ההזמנה</h2>
              <div className="space-y-4">
                {order.items?.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 pb-4 border-b last:border-b-0">
                    <img
                      src={item.product?.images?.[0] || '/placeholder-product.png'}
                      alt={item.product?.name}
                      className="w-20 h-20 object-cover rounded"
                      onError={(e) => { e.target.src = '/placeholder-product.png'; }}
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">
                        {item.product?.name || item.product_snapshot?.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        כמות: {item.quantity}
                      </p>
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">
                        {formatPrice(item.unit_price)}
                      </p>
                      <p className="text-sm text-gray-600">
                        סה"כ: {formatPrice(item.total_price)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Tracking */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <OrderTracking orderId={orderId} showTitle={true} />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">סיכום הזמנה</h2>
              <div className="space-y-3">
                <div className="flex justify-between text-gray-700">
                  <span>סכום ביניים:</span>
                  <span>{formatPrice(order.subtotal)}</span>
                </div>
                {order.discount_amount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>הנחה:</span>
                    <span>-{formatPrice(order.discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-700">
                  <span>משלוח:</span>
                  <span>{formatPrice(order.shipping_amount)}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>מע"מ:</span>
                  <span>{formatPrice(order.tax_amount)}</span>
                </div>
                <div className="pt-3 border-t flex justify-between text-lg font-bold text-gray-900">
                  <span>סה"כ לתשלום:</span>
                  <span>{formatPrice(order.total_amount)}</span>
                </div>
              </div>
            </div>

            {/* Order Info */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">פרטי הזמנה</h2>
              <div className="space-y-3 text-sm">
                {(order.company?.name || order.customer?.company_name) && (
                  <div>
                    <span className="text-gray-600">חברה:</span>
                    <p className="font-semibold">{order.company?.name || order.customer?.company_name}</p>
                  </div>
                )}
                <div>
                  <span className="text-gray-600">תאריך הזמנה:</span>
                  <p className="font-semibold">{formatDate(order.createdAt)}</p>
                </div>
                <div>
                  <span className="text-gray-600">אמצעי תשלום:</span>
                  <p className="font-semibold">{order.payment_method}</p>
                </div>
                <div>
                  <span className="text-gray-600">סטטוס תשלום:</span>
                  <p className="font-semibold">{order.payment_status}</p>
                </div>
                {order.notes && (
                  <div>
                    <span className="text-gray-600">הערות:</span>
                    <p className="font-semibold">{order.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Shipping Address */}
            {order.shippingAddress && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">כתובת משלוח</h2>
                <div className="text-sm space-y-1">
                  <p>{order.shippingAddress.full_name}</p>
                  <p>{order.shippingAddress.street}</p>
                  <p>{order.shippingAddress.city}, {order.shippingAddress.postal_code}</p>
                  {order.shippingAddress.phone && <p>טלפון: {order.shippingAddress.phone}</p>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Track Link for sharing */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-gray-700 mb-2">
            <strong>עקוב אחרי ההזמנה מכל מקום:</strong>
          </p>
          <Link
            to={`/track/${order.order_number}`}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            {window.location.origin}/track/{order.order_number}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
